import { computed, inject, InjectionToken, Type } from '@angular/core';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { ItemId } from 'products-data';
import { SignalStoreFeatureType } from 'state-data';
import { CartComponentStore } from '../../cart/cart.component.store';
import { WithCartFeature } from '../../cart/features';

interface CartItemState {
  itemId: ItemId | null;
}

export interface WithCartItemConfig {
  cartStoreType: Type<WithCartFeature>;
}

export const CART_STORE_TYPE = new InjectionToken<Type<WithCartFeature>>('CART_STORE_TYPE');

export const defaultWithCartItemConfig: WithCartItemConfig = {
  cartStoreType: CartComponentStore,
};

export const withCartItem = (config?: Partial<WithCartItemConfig>) => {
  return signalStoreFeature(
    withState<CartItemState>({
      itemId: null,
    }),

    withMethods((store) => {
      const cartStoreType = config?.cartStoreType ?? inject(CART_STORE_TYPE, { optional: true }) ?? defaultWithCartItemConfig.cartStoreType;
      const cfg = { ...defaultWithCartItemConfig, ...config, cartStoreType } as WithCartItemConfig;
      const cartStore = inject(cfg.cartStoreType);
      const selectByItemId = (itemId: ItemId | null) => {
        patchState(store, { itemId });
      };

      const updateItemQuantity = (quantity: number) => {
        const id = store.itemId();
        if (id) {
          cartStore.updateItemQuantity(id, quantity);
        }
      };

      return { selectByItemId, updateItemQuantity };
    }),

    withComputed((store) => {
      const cartStoreType = config?.cartStoreType ?? inject(CART_STORE_TYPE, { optional: true }) ?? defaultWithCartItemConfig.cartStoreType;
      const cfg = { ...defaultWithCartItemConfig, ...config, cartStoreType } as WithCartItemConfig;

      const cartStore = inject(cfg.cartStoreType);
      const item = computed(() => {
        const id = store.itemId();
        if (!id) {
          return null;
        }
        const items = cartStore.cartItems();
        return items.find((i) => i.id.equals(id)) || null;
      });

      const cartItemInCart = computed(() => !!item());
      const cartItemQuantity = computed(() => item()?.quantity ?? 0);

      const cartItemLoading = computed(() => cartStore.cartLoading());
      const cartItemError = computed(() => cartStore.cartError());
      const cartItemUpdating = computed(() => {
        const id = store.itemId();
        return id ? cartStore.isItemPending(id) : false;
      });

      return { cartItemQuantity, cartItemLoading, cartItemError, cartItemUpdating, cartItemInCart };
    }),
  );
};

export type WithCartItemFeature = SignalStoreFeatureType<ReturnType<typeof withCartItem>>;
